"use client";

import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

// Indicatifs FR + voisins francophones. Le 1er est le défaut.
export const COUNTRIES = [
  { code: "FR", flag: "🇫🇷", dial: "+33" },
  { code: "BE", flag: "🇧🇪", dial: "+32" },
  { code: "CH", flag: "🇨🇭", dial: "+41" },
  { code: "LU", flag: "🇱🇺", dial: "+352" },
  { code: "CA", flag: "🇨🇦", dial: "+1" },
  { code: "MA", flag: "🇲🇦", dial: "+212" },
];

// Normalise : accepte "06…", "+336…", "00336…" -> renvoie du E.164 (+336…)
export function normalizePhone(raw: string, dial: string): string {
  let v = raw.replace(/[\s.\-()]/g, "");
  if (!v) return "";
  if (v.startsWith("+")) return v;
  if (v.startsWith("00")) return "+" + v.slice(2);
  if (v.startsWith("0")) return dial + v.slice(1);
  return dial + v;
}

export function PhoneField({
  onChange,
  hint,
  initialValue = "",
  invalid = false,
}: {
  onChange: (normalized: string) => void;
  hint: string; // pourquoi on demande le numéro (varie selon le funnel)
  /** Numéro pré-rempli (lien personnalisé). Accepte 06… comme +336… */
  initialValue?: string;
  invalid?: boolean;
}) {
  // Un numéro pré-rempli au format international pré-sélectionne son indicatif,
  // sinon l'affichage dirait « +33 » devant un numéro belge.
  const [country, setCountry] = useState(
    () =>
      (initialValue.startsWith("+")
        ? [...COUNTRIES]
            .sort((a, b) => b.dial.length - a.dial.length)
            .find((c) => initialValue.startsWith(c.dial))
        : undefined) ?? COUNTRIES[0]
  );
  const [raw, setRaw] = useState(initialValue);

  const push = (r: string, dial: string) => {
    setRaw(r);
    onChange(normalizePhone(r, dial));
  };

  // Un numéro pré-rempli doit être normalisé même si le visiteur n'y touche
  // pas : sans ça, « 0612345678 » partirait tel quel au webhook au lieu de
  // « +33612345678 », et le hachage téléphone des régies ne correspondrait plus.
  useEffect(() => {
    if (initialValue) onChange(normalizePhone(initialValue, country.dial));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium text-ink/55">
        Téléphone ({hint})
      </span>
      <div
        className={cn(
          "flex items-stretch overflow-hidden rounded-xl border bg-ink/[0.03] transition focus-within:border-gold",
          invalid ? "border-red-500/70" : "border-ink/15"
        )}
      >
        <select
          aria-label="Indicatif pays"
          value={country.code}
          onChange={(e) => {
            const c = COUNTRIES.find((x) => x.code === e.target.value)!;
            setCountry(c);
            push(raw, c.dial);
          }}
          className="border-r border-ink/15 bg-transparent py-3 pl-3 pr-1 text-sm text-ink outline-none"
        >
          {COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>
              {c.flag} {c.dial}
            </option>
          ))}
        </select>
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={raw}
          onChange={(e) => push(e.target.value, country.dial)}
          placeholder="06 12 34 56 78"
          className="w-full bg-transparent px-3.5 py-3 text-sm text-ink outline-none placeholder:text-ink/40"
        />
      </div>
      <span className="mt-1 block text-[11px] text-ink/45">
        Format local (06…) ou international (+33…)
      </span>
    </label>
  );
}
